import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, Action } from '@ngrx/store';
import { of, Observable } from 'rxjs';
import { map, switchMap, catchError, withLatestFrom, tap } from 'rxjs/operators';
import { WorkflowApiService } from './workflow-api.service';
import * as workflowActions from './workflow.actions';
import * as workflowSelectors from './workflow.selectors';
import * as fromRouting from '../../../core/store/routing';
import {
  WorkflowStepRule,
  WorkflowRuleEvent,
  WorkflowRuleScheduleTrigger,
  Workflow,
} from 'src/app/modules/core/store/models/workflow.model';
import produce from 'immer';
import { v4 as uuidv4 } from 'uuid';
import { GUID_EMPTY } from '@app/core/constants';

@Injectable()
export class WorkflowEffects {
  constructor(
    private actions$: Actions,
    private workflowApiService: WorkflowApiService,
    private store$: Store<any>
  ) {}

  getWorkflowForDataset$ = createEffect(() =>
    this.actions$.pipe(
      ofType(workflowActions.getWorkflowForDataset),
      switchMap(action => {
        return this.workflowApiService.getWorkflowForDataset(action.datasetUId).pipe(
          map(results => {
            const workflow = results?.data[0];

            // ensure the array order matches the
            // sort orders for steps and actions
            workflow?.workflowSteps?.sort((a, b) => a.sortOrder - b.sortOrder);
            workflow?.workflowSteps?.map(step => {
              step?.actions?.sort((a, b) => a.sortOrder - b.sortOrder);
              step?.rules?.sort((a, b) => a.sortOrder - b.sortOrder);
              step?.rules?.map(rule => rule?.actions?.sort((a, b) => a.sortOrder - b.sortOrder));
            });

            return workflowActions.getWorkflowForDatasetComplete({ workflow });
          }),
          catchError(err => of(workflowActions.setError({ err })))
        );
      })
    )
  );
  saveSelectedWorkflow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(workflowActions.saveSelectedWorkflow),
      withLatestFrom(this.store$.select(workflowSelectors.selectSelectedWorkflow)),
      switchMap(([action, workflow]) => {
        if (workflow.uId === GUID_EMPTY) {
          return [workflowActions.setIsWorking({ isWorking: true }), workflowActions.createWorkflow({ workflow, close: action.close })];
        } else {
          return [workflowActions.setIsWorking({ isWorking: true }), workflowActions.updateWorkflow({ workflow, close: action.close })];
        }
      })
    )
  );

  createWorkflow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(workflowActions.createWorkflow),
      switchMap(action => {
        return this.workflowApiService.createWorkflow(action.workflow).pipe(
          switchMap(workflow => {
            return [
              workflowActions.setSelectedWorkflow({ workflow: action.close ? null : workflow }),
              workflowActions.setTableChanged({ changed: true })
            ];
          }),
          catchError(err => of(workflowActions.setError({ err })))
        );
      })
    )
  );

  updateWorkflow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(workflowActions.updateWorkflow),
      switchMap(action => {
        return this.workflowApiService.updateWorkflow(action.workflow).pipe(
          switchMap(workflow => {
            return [
              workflowActions.setSelectedWorkflow({ workflow: action.close ? null : workflow }),
              workflowActions.setTableChanged({ changed: true })
            ];
          }),
          catchError(err => of(workflowActions.setError({ err })))
        );
      })
    )
  );

  setWorkflowActive$ = createEffect(() =>
    this.actions$.pipe(
      ofType(workflowActions.setWorkflowActive),
      switchMap(action => {
        return this.workflowApiService.getFullWorkflow(action.workflow.uId).pipe(
          map(results => {
            if (!(results?.data?.length > 0)) {
              return;
            }
            const updatedWorkflow = produce(results?.data[0], draftState => {
              draftState.isActive = action.active;
            });
            return workflowActions.updateWorkflow({ workflow: updatedWorkflow, close: false });
          }),
          catchError(err => of(workflowActions.setError({ err })))
        );
      })
    )
  );

  setWorkflowDefault$ = createEffect(() =>
    this.actions$.pipe(
      ofType(workflowActions.setWorkflowDefault),
      switchMap(action => {
        return this.workflowApiService.getFullWorkflow(action.workflow.uId).pipe(
          map(results => {
            if (!(results?.data?.length > 0)) {
              return;
            }
            const updatedWorkflow = produce(results?.data[0], draftState => {
              draftState.isDefault = action.default;
            });
            return workflowActions.updateWorkflow({ workflow: updatedWorkflow, close: false });
          }),
          catchError(err => of(workflowActions.setError({ err })))
        );
      })
    )
  );

  editSelectedWorkflow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(workflowActions.editSelectedWorkflow),
      withLatestFrom(this.store$.select(workflowSelectors.selectSelectedWorkflow)),
      map(([action, workflow]) => {
        const workflowUId = workflow.uId;

        return fromRouting.go({
          routeData: { path: ['management', 'workflow', workflowUId] }
        });
      })
    )
  );

  // Rules
  addWorkflowStepRule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(workflowActions.addWorkflowStepRule),
      switchMap(action => {
        let maxSortOrder = action.step.rules?.length;
        const rule: WorkflowStepRule = {
          uId: uuidv4(),
          name: '',
          ruleEvent: WorkflowRuleEvent.StepStarted,
          scheduleTrigger: WorkflowRuleScheduleTrigger.StepStarted,
          sortOrder: maxSortOrder,
          actions: []
        };
        return [workflowActions.setSelectedWorkflowStepRule({ step: action.step, rule })];
      })
    )
  );

  editWorkflowStepRule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(workflowActions.editWorkflowStepRule),
      map(action => {
        return workflowActions.setSelectedWorkflowStepRule({ step: action.step, rule: action.rule });
      })
    )
  );

  saveWorkflowStepRule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(workflowActions.saveWorkflowStepRule),
      withLatestFrom(this.store$.select(workflowSelectors.selectSelectedWorkflow)),
      map(([action, workflow]) => {
        let newWorkflow = null;
        const step = workflow.workflowSteps.find(s => s.uId === action.step.uId);

        if (!step.rules?.find(r => r.uId === action.rule.uId)) {
          newWorkflow = produce(workflow, draftState => {
            const step = draftState.workflowSteps.find(ws => ws.uId === action.step.uId);
            if (!!!step.rules) {
              step.rules = [];
            }
            let sortOrder = 0;
            step.rules.map(a => (a.sortOrder = sortOrder++));
            step.rules.push(action.rule);
          });
        } else {
          newWorkflow = produce(workflow, draftState => {
            const step = draftState.workflowSteps.find(ws => ws.uId === action.step.uId);
            const ruleIndex = step.rules.findIndex(a => a.uId === action.rule.uId);
            step.rules[ruleIndex] = action.rule;
          });
        }
        return workflowActions.setUpdatedWorkflow({ workflow: newWorkflow });
      })
    )
  );

  deleteSelectedWorkflowStepRule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(workflowActions.deleteSelectedWorkflowStepRule),
      withLatestFrom(
        this.store$.select(workflowSelectors.selectSelectedWorkflow),
        this.store$.select(workflowSelectors.selectSelectedWorkflowStep),
        this.store$.select(workflowSelectors.selectWorkflowStepRuleForDelete)
      ),
      map(([action, workflow, workflowStep, rule]) => {
        const newWorkflow = produce(workflow, draftState => {
          const step = draftState.workflowSteps.find(s => s.uId === workflowStep.uId);
          const ruleToRemove = step.rules.findIndex(a => a.uId === rule.uId);
          step.rules.splice(ruleToRemove, 1);
        });
        return workflowActions.setUpdatedWorkflow({ workflow: newWorkflow });
      })
    )
  );

  loadWorkflowRuleImplementations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(workflowActions.loadWorkflowRuleImplementations),
      switchMap(action => {
        return this.workflowApiService.getRuleImplementations(action.parentType).pipe(
          map(results => {
            return workflowActions.loadWorkflowRuleImplementationsComplete({ ruleImplementations: results });
          }),
          catchError(err => of(workflowActions.setError({ err })))
        );
      })
    )
  );

  editWorkflow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(workflowActions.editWorkflow),
      switchMap(action => {
        const { workflow } = action;

        return this.workflowApiService.getFullWorkflow(workflow.uId).pipe(
          map(results => {
            return workflowActions.setSelectedWorkflow({ workflow: results?.data[0] });
          }),
          catchError(err => of(workflowActions.setError({ err })))
        );
      })
    )
  );

  deleteWorkflow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(workflowActions.deleteWorkflow),
      switchMap(action => {
        const { workflow } = action;

        return this.workflowApiService.deleteWorkflow(workflow.uId).pipe(
          switchMap(results => {
            return [workflowActions.setSelectedWorkflow({ workflow: null }), workflowActions.setTableChanged({ changed: true })];
          }),
          catchError(err => of(workflowActions.setError({ err })))
        );
      })
    )
  );

  newWorkflow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(workflowActions.newWorkflow),
      switchMap(action => {
        const workflow: Workflow = {
          uId: GUID_EMPTY,
          name: action.name,
          description: '',
          isActive: true,
          parentType: action.parentType
        };

        return this.workflowApiService.initiateWorkflow(workflow).pipe(
          map(result => {
            return workflowActions.newWorkflowComplete({ workflow: result });
          }),
          catchError(err => of(workflowActions.setError({ err })))
        );
      })
    )
  );

  getStatuses$ = createEffect(() =>
    this.actions$.pipe(
      ofType(workflowActions.getStatuses),
      switchMap(action => {
        return this.workflowApiService.getWorkflowStatuses(action.queryParams, true).pipe(
          map(res => workflowActions.loadStatuses(res)),
          catchError(err => of(workflowActions.setError({ err })))
        );
      })
    )
  );
}
